/**
 * ModuleProgress — json-render web wrapper
 *
 * Web implementation for the json-render catalog (web-only).
 * Progress indicator for modules building baseline data.
 * Maps to: analysis-results/ModuleProgressCard.tsx
 *
 * Task: NocoDB #2084
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React from "react";
import { useTranslation } from "@dramadetector/shared";
import type { ModuleProgressProps } from "../schema";

const CONFIDENCE_KEYS = {
	none: "progressive_disclosure.building_baseline",
	low: "progressive_disclosure.early_insight",
	medium: "progressive_disclosure.growing_confidence",
	high: "progressive_disclosure.full_confidence",
} as const;

export function ModuleProgress({
	moduleName,
	current,
	required,
	confidence = "none",
	isPremium = true,
}: ModuleProgressProps) {
	const { t } = useTranslation("analysis") as {
		t: (key: string, options?: any) => string;
	};
	const pct = required > 0 ? Math.min((current / required) * 100, 100) : 0;

	if (!isPremium) {
		return (
			<div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-4">
				<h4 className="text-sm font-semibold text-[#F3F4F6]">{moduleName}</h4>
				<p className="text-xs text-[#9CA3AF] mt-1">
					{t("progressive_disclosure.upgrade_to_unlock")}
				</p>
			</div>
		);
	}

	return (
		<div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
			<div className="flex items-center justify-between">
				<h4 className="text-sm font-semibold text-[#F3F4F6]">{moduleName}</h4>
				<span className="text-xs text-[#9CA3AF]">
					{t("progressive_disclosure.analyses_count", { current, required })}
				</span>
			</div>
			<div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
				<div
					className="h-full rounded-full bg-[#8b5cf6] transition-all duration-300"
					style={{ width: `${pct}%` }}
				/>
			</div>
			<p className="text-xs text-[#9CA3AF]">{t(CONFIDENCE_KEYS[confidence])}</p>
		</div>
	);
}
